import type {
  MedPatientBackground,
  MedPatientBackgroundUpdate,
  MedVisit,
  MedVisitBulkCopyRequest,
  MedVisitBulkCopyResponse,
  MedVisitBulkDeleteRequest,
  MedVisitBulkDeleteResponse,
  MedVisitBulkUpdatePatch,
  MedVisitBulkUpdateRequest,
  MedVisitBulkUpdateResponse,
  MedVisitDeleteResponse,
  MedVisitDetail,
  MedVisitDetailResponse,
} from "../types/medchron";
import type { APIInstance } from "./apiService";
import {
  MedVisitCitationSchema,
  type MedVisitCreateRequest,
  type MedVisitCreateResponse,
  type MedVisitUpdateRequest,
} from "./medChronServiceV2.types";

const MedVisitCitationsSchema = MedVisitCitationSchema.array();

/**
 * Validates a visit's citations against the schema.
 * - Throws an error naming the visit when any citation is malformed, failing the whole response.
 */
const withParsedCitations = (visit: MedVisit): MedVisit => {
  const parsed = MedVisitCitationsSchema.safeParse(visit.citations);
  if (!parsed.success) {
    throw new Error(`Invalid citations for visit ${visit.visit_id}: ${parsed.error.message}`);
  }
  return { ...visit, citations: parsed.data };
};

export class MedChronServiceV2 {
  private api: APIInstance;

  constructor(api: APIInstance) {
    this.api = api;
  }

  async getVisits(caseId: string, versionId: string): Promise<MedVisit[]> {
    const response = await this.api.get<MedVisit[]>(
      `/cases/${caseId}/versions/${versionId}/visits`
    );
    return response.data.map(withParsedCitations);
  }

  async updateVisitDetail(
    caseId: string,
    versionId: string,
    visitId: string,
    oldCategory: string,
    detailData: MedVisitDetail
  ): Promise<MedVisitDetailResponse> {
    const response = await this.api.put<MedVisitDetailResponse>(
      `/cases/${caseId}/versions/${versionId}/visits/${visitId}/details/${oldCategory}`,
      detailData
    );
    return response.data;
  }

  async createVisit(
    versionId: string,
    visitData: MedVisitCreateRequest
  ): Promise<MedVisitCreateResponse> {
    const response = await this.api.post<MedVisitCreateResponse>(
      `/versions/${versionId}/visits`,
      visitData
    );
    return response.data;
  }

  async updateVisit(
    versionId: string,
    visitId: string,
    visitData: MedVisitUpdateRequest
  ): Promise<MedVisit> {
    const response = await this.api.put<MedVisit>(
      `/versions/${versionId}/visits/${visitId}`,
      visitData
    );
    return withParsedCitations(response.data);
  }

  async deleteVisit(versionId: string, visitId: string): Promise<MedVisitDeleteResponse> {
    const response = await this.api.delete<MedVisitDeleteResponse>(
      `/versions/${versionId}/visits/${visitId}`
    );
    return response.data;
  }

  async addVisitDetail(
    caseId: string,
    versionId: string,
    visitId: string,
    detailData: MedVisitDetail
  ): Promise<MedVisitDetailResponse> {
    const response = await this.api.post<MedVisitDetailResponse>(
      `/cases/${caseId}/versions/${versionId}/visits/${visitId}/details`,
      detailData
    );
    return response.data;
  }

  async getPatientBackground(caseId: string, versionId: string): Promise<MedPatientBackground> {
    const response = await this.api.get<MedPatientBackground>(
      `/cases/${caseId}/versions/${versionId}/patient-background`
    );
    return response.data;
  }

  async patchPatientBackground(
    caseId: string,
    versionId: string,
    data: MedPatientBackgroundUpdate
  ): Promise<MedPatientBackground> {
    const response = await this.api.patch<MedPatientBackground>(
      `/cases/${caseId}/versions/${versionId}/patient-background`,
      data
    );
    return response.data;
  }

  async bulkUpdateVisits(
    caseId: string,
    versionId: string,
    visitIds: string[],
    patch: MedVisitBulkUpdatePatch
  ): Promise<MedVisitBulkUpdateResponse> {
    const body: MedVisitBulkUpdateRequest = { visit_ids: visitIds, patch };
    const response = await this.api.patch<MedVisitBulkUpdateResponse>(
      `/cases/${caseId}/versions/${versionId}/visits/bulk`,
      body
    );
    return response.data;
  }

  async bulkDeleteVisits(
    caseId: string,
    versionId: string,
    visitIds: string[]
  ): Promise<MedVisitBulkDeleteResponse> {
    const body: MedVisitBulkDeleteRequest = { visit_ids: visitIds };
    const response = await this.api.post<MedVisitBulkDeleteResponse>(
      `/cases/${caseId}/versions/${versionId}/visits/bulk-delete`,
      body
    );
    return response.data;
  }

  async bulkCopyVisits(
    targetVersionId: string,
    sourceVersionId: string,
    visitIds: string[]
  ): Promise<MedVisitBulkCopyResponse> {
    const body: MedVisitBulkCopyRequest = { visit_ids: visitIds };
    const response = await this.api.post<MedVisitBulkCopyResponse>(
      `/versions/${targetVersionId}/visits/copy-from/${sourceVersionId}`,
      body
    );
    return response.data;
  }
}
